import axios from 'axios';

const GEMINI_API_KEY = 'AIzaSyC2IImVKFNsz4hQHw6tpqQcFffn2T7LzU4';
const API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent';

const SYSTEM_CONTEXT = `You are an expert accounting AI assistant working for Accounting Helpdesk.
Your role is to provide professional accounting advice and guidance.

IMPORTANT DISCLAIMER: Always remind users that accounting is a highly sensitive area and they should NOT fully rely on AI advice alone. Always recommend contacting an Accounting HelpDesk expert for final verification and professional confirmation of any accounting matters.

Key traits:
- Professional and courteous
- Expert knowledge of accounting principles, tax laws, and financial regulations
- Provide clear, concise explanations
- Always emphasize the need to verify information with a human expert
- Always mention that advice is for informational purposes only and requires professional validation
- Keep responses focused on accounting and business finance topics

Scope limitations:
- If the user's question is not related to accounting, politely explain that you are specifically an accounting expert and can only assist with accounting, tax, and financial matters
- Redirect non-accounting questions back to accounting topics

Company services:
- Quick Question Service (€10 per question)
- Full Bookkeeping
- Account Reviews
- Tax Declarations
- Payroll Management
- Company Formation
- Auditing Services

Language: Respond in the same language as the user's question (French or English)

Always end responses by encouraging users to contact an Accounting HelpDesk expert for professional confirmation.`;

interface ChatMessage {
  isUser: boolean;
  content: string;
}

interface GeminiResponse {
  candidates: Array<{
    content: {
      parts: Array<{
        text: string;
      }>;
    };
  }>;
}

export const createGeminiMessage = async (userMessage: string, language: string, previousMessages: Array<ChatMessage> = []) => {
  try {
    // Format des messages selon la documentation de l'API Gemini
    const formattedMessages = previousMessages.map(msg => ({
      role: msg.isUser ? 'user' : 'model',
      parts: [{ text: msg.content || '' }]
    }));

    const payload = {
      contents: [
        ...formattedMessages,
        {
          role: 'user',
          parts: [{ text: userMessage }]
        }
      ],
      generationConfig: {
        temperature: 0.7,
        topK: 40,
        topP: 0.95,
        maxOutputTokens: 1024,
      },
      safetySettings: [
        {
          category: "HARM_CATEGORY_HARASSMENT",
          threshold: "BLOCK_ONLY_HIGH"
        },
        {
          category: "HARM_CATEGORY_HATE_SPEECH",
          threshold: "BLOCK_ONLY_HIGH"
        },
        {
          category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
          threshold: "BLOCK_ONLY_HIGH"
        },
        {
          category: "HARM_CATEGORY_DANGEROUS_CONTENT",
          threshold: "BLOCK_ONLY_HIGH"
        }
      ],
      systemInstruction: {
        parts: [{ text: SYSTEM_CONTEXT }]
      }
    }

    console.log('Sending request to Gemini API:', JSON.stringify(payload));

    const response = await axios.post<GeminiResponse>(
      `${API_URL}?key=${GEMINI_API_KEY}`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
        }
      }
    );

    console.log('Response from Gemini API:', response.data);

    const messageText = response.data.candidates?.[0]?.content?.parts?.[0]?.text;
    return messageText || 'Je suis désolé, je ne peux pas traiter votre demande pour le moment.';
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('Error calling Gemini API:', errorMessage);
    return language === 'fr' 
      ? 'Désolé, je ne peux pas traiter votre demande pour le moment. Veuillez réessayer plus tard.'
      : 'Sorry, I cannot process your request at the moment. Please try again later.';
  }
};
